"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useScroll, useSpring } from "framer-motion";

/**
 * DotLottie player scrubbed by scroll.
 *
 * Progress 0 when the top of the Lottie hits 90% of the viewport
 * (the file has reached 10% of the screen from the bottom).
 * Progress 1 when the top hits 10% of the viewport (before 90% of
 * the target's travel toward the top). `scrollTo` (0–1) maps that
 * range onto a portion of the frames (default: all frames).
 */
export default function LottieAnimation({
  src,
  scrollTo = 1,
  className = "",
  style,
}) {
  const containerRef = useRef(null);
  const dotLottieRef = useRef(null);
  const loadHandlerRef = useRef(null);
  const [totalFrames, setTotalFrames] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 0.80", "start 0.20"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const handleDotLottieRef = useCallback((dotLottie) => {
    const previous = dotLottieRef.current;
    if (previous && loadHandlerRef.current) {
      previous.removeEventListener("load", loadHandlerRef.current);
    }

    dotLottieRef.current = dotLottie;
    if (!dotLottie) return;

    const onLoad = () => {
      setTotalFrames(dotLottie.totalFrames);
      dotLottie.pause();
    };

    loadHandlerRef.current = onLoad;
    dotLottie.addEventListener("load", onLoad);
    if (dotLottie.isLoaded) {
      onLoad();
    }
  }, []);

  useEffect(() => {
    if (totalFrames <= 0) return undefined;

    return smoothProgress.on("change", (latest) => {
      const player = dotLottieRef.current;
      if (!player) return;

      const progress = Math.min(1, Math.max(0, latest));
      const lastFrame = Math.max(totalFrames - 1, 0);
      player.setFrame(progress * lastFrame * scrollTo);
    });
  }, [smoothProgress, totalFrames, scrollTo]);

  return (
    <div ref={containerRef} className={className} style={style}>
      <DotLottieReact
        src={src}
        autoplay={false}
        loop={false}
        className="h-full w-full"
        dotLottieRefCallback={handleDotLottieRef}
      />
    </div>
  );
}
